import { useState, useEffect } from "react";
import TicketStatus from "../TicketStatus/TicketStatus";
import getTime from "../../../helpers/getTime.js";
import apiClient from "../../../api/client";
import { useAuth } from "../../../hooks/useAuth";
import { updateTicket } from "../../../api/ticketApi.js";
import toast from "react-hot-toast";

/**
 * Ticket Details Component
 * Updated for v2.0 API structure
 */
function Details({ ticket, showAddAttachmentsButton, onAddAttachmentsClick, onDescriptionUpdated }) {
    const { user, isSuperAdmin } = useAuth();
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState(ticket?.description || "");
    const [isSaving, setIsSaving] = useState(false);

    const isOwner = ticket?.creatorId === user?.id;
    const canEdit = isSuperAdmin || isOwner;
    
    /**
     * Handle attachment download
     */
    const handleDownload = async (attachment) => {
        try {
            const response = await apiClient.get(
                `/tickets/attachment/${attachment.id}`,
                { responseType: "blob" }
            );
            
            // Create blob and download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", attachment.fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download error:", error);
            // Extract proper error message from API response
            let errorMessage = "Failed to download attachment";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            alert(errorMessage);
        }
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: "#28a745",
            medium: "#ffa500",
            high: "#ff6b6b",
            urgent: "#dc3545",
        };
        return colors[priority] || "#6c757d";
    };

    /**
     * Handle description save
     */
    const handleSaveDescription = async () => {
        if (!editedDescription.trim()) {
            toast.error("Description cannot be empty");
            return;
        }
        
        try {
            setIsSaving(true);
            const response = await updateTicket(ticket.id, { description: editedDescription.trim() });
            
            if (response.success) {
                toast.success("Description updated successfully");
                setIsEditingDescription(false);
                if (onDescriptionUpdated) {
                    onDescriptionUpdated();
                }
            } else {
                toast.error(response.message || "Failed to update description");
            }
        } catch (error) {
            console.error("Error updating description:", error);
            let errorMessage = "Failed to update description";
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    // Update edited description when ticket changes
    useEffect(() => {
        if (ticket?.description) {
            setEditedDescription(ticket.description);
        }
    }, [ticket?.description]);

    return (
        <div className="details" style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px"
        }}>
            {/* Description - Compact */}
            <div style={{
                padding: "12px 16px",
                background: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #e5e7eb"
            }}>
                <div style={{ 
                    fontSize: "9px", 
                    color: "#6b7280", 
                    marginBottom: "8px", 
                    fontWeight: "500", 
                    textTransform: "uppercase",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}>
                    <span>DESCRIPTION</span>
                    {canEdit && !isEditingDescription && (
                        <button
                            onClick={() => setIsEditingDescription(true)}
                            style={{
                                padding: "4px 8px",
                                background: "transparent",
                                color: "#6b7280",
                                border: "1px solid #d1d5db",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: "10px",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px"
                            }}
                            title="Edit description"
                        >
                            <i className="las la-pen" style={{ fontSize: "10px" }} />
                        </button>
                    )}
                </div>
                {isEditingDescription ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <textarea
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            style={{
                                width: "100%",
                                minHeight: "120px",
                                padding: "8px 12px",
                                border: "1px solid #d1d5db",
                                borderRadius: "6px",
                                fontSize: "16px",
                                lineHeight: "1.6",
                                color: "#374151",
                                fontFamily: "inherit",
                                resize: "vertical"
                            }}
                            autoFocus
                        />
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button
                                onClick={() => {
                                    setIsEditingDescription(false);
                                    setEditedDescription(ticket?.description || "");
                                }}
                                disabled={isSaving}
                                style={{
                                    padding: "6px 12px",
                                    background: "transparent",
                                    color: "#6b7280",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "6px",
                                    cursor: isSaving ? "not-allowed" : "pointer",
                                    fontSize: "12px",
                                    fontWeight: "500"
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveDescription}
                                disabled={isSaving}
                                style={{
                                    padding: "6px 12px",
                                    background: "#3bac75",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: isSaving ? "not-allowed" : "pointer",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    opacity: isSaving ? 0.6 : 1
                                }}
                            >
                                <i className="las la-check" /> {isSaving ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{ lineHeight: "1.6", color: "#374151", whiteSpace: "pre-wrap", fontSize: "16px" }}>
                        {ticket.description}
                    </div>
                )}
            </div>

            {/* Tags - Compact (if exists) */}
            {ticket.tags && ticket.tags.length > 0 && (
                <div style={{
                    padding: "12px 16px",
                    background: "#ffffff",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb"
                }}>
                    <div style={{ fontSize: "9px", color: "#6b7280", marginBottom: "8px", fontWeight: "500", textTransform: "uppercase" }}>TAGS</div>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                            {ticket.tags.map((tag, index) => (
                                <span
                                    key={index}
                                    style={{
                                        background: "#e5e7eb",
                                    padding: "4px 10px",
                                    borderRadius: "4px",
                                    fontSize: "10.8px",
                                        color: "#374151",
                                        fontWeight: "500"
                                    }}
                                >
                                    {tag}
                                </span>
                            ))}
                    </div>
                </div>
            )}
            
            {/* Display attachments if any */}
            {ticket.attachments && ticket.attachments.length > 0 && (
                <div className="detail" style={{
                    gridColumn: "1 / -1",
                    padding: "0",
                    background: "#ffffff",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                    overflow: "hidden"
                }}>
                    {/* Table Container - Full Width Grid Layout */}
                    <div style={{ 
                        display: "grid",
                        gridTemplateColumns: "56px 1fr 98px 126px 112px",
                        width: "100%",
                        background: "#ffffff"
                    }}>
                        {/* Header Row */}
                        <div style={{
                            gridColumn: "1 / -1",
                            display: "grid",
                            gridTemplateColumns: "56px 1fr 98px 126px 112px",
                            background: "#f9fafb",
                            borderBottom: "2px solid #e5e7eb",
                            padding: "11px 17px",
                            fontWeight: "600",
                            fontSize: "8px",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px"
                        }}>
                            <div>Type</div>
                            <div>File Name</div>
                            <div>Size</div>
                            <div>Uploaded By</div>
                            <div style={{ textAlign: "center" }}>Action</div>
                        </div>

                        {/* Attachment Rows */}
                        {ticket.attachments.map((attachment, index) => {
                            // Get file icon based on extension
                            const getFileIcon = (fileName) => {
                                const ext = fileName.split('.').pop().toLowerCase();
                                if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
                                    return { icon: 'las la-image', color: '#10b981', bg: '#d1fae5' };
                                }
                                if (['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', 'mp3', 'wav'].includes(ext)) {
                                    return { icon: 'las la-video', color: '#ef4444', bg: '#fee2e2' };
                                }
                                if (ext === 'pdf') {
                                    return { icon: 'las la-file-pdf', color: '#dc2626', bg: '#fee2e2' };
                                }
                                if (['doc', 'docx'].includes(ext)) {
                                    return { icon: 'las la-file-word', color: '#2563eb', bg: '#dbeafe' };
                                }
                                if (['xls', 'xlsx'].includes(ext)) {
                                    return { icon: 'las la-file-excel', color: '#16a34a', bg: '#dcfce7' };
                                }
                                return { icon: 'las la-file', color: '#6b7280', bg: '#f3f4f6' };
                            };

                            // Format file size
                            const formatFileSize = (bytes) => {
                                if (bytes < 1024) return bytes + ' B';
                                if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
                                return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
                            };

                            const fileIcon = getFileIcon(attachment.fileName);

                            return (
                                <div
                                    key={attachment.id}
                                    style={{
                                        gridColumn: "1 / -1",
                                        display: "grid",
                                        gridTemplateColumns: "56px 1fr 98px 126px 112px",
                                        padding: "14px 17px",
                                        borderBottom: index < ticket.attachments.length - 1 ? "1px solid #f3f4f6" : "none",
                                        transition: "all 0.2s",
                                        cursor: "default",
                                        alignItems: "center"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = "#f9fafb";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = "#ffffff";
                                    }}
                                >
                                    {/* File Type Icon */}
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <div style={{
                                            width: "34px",
                                            height: "34px",
                                            borderRadius: "8px",
                                            background: fileIcon.bg,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                                        }}>
                                            <i className={fileIcon.icon} style={{ fontSize: "15px", color: fileIcon.color }} />
                                        </div>
                                    </div>

                                    {/* File Name - Takes remaining space */}
                                    <div style={{ 
                                        minWidth: 0,
                                        paddingRight: "11px"
                                    }}>
                                        <div style={{
                                            fontWeight: "600",
                                            fontSize: "11px",
                                            color: "#111827",
                                            marginBottom: "4px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {attachment.fileName}
                                        </div>
                                        <div style={{
                                            fontSize: "8px",
                                            color: "#9ca3af",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px"
                                        }}>
                                            <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                                                <i className="las la-weight" style={{ fontSize: "8px" }} />
                                                {formatFileSize(attachment.fileSize)}
                                            </span>
                                            <span>•</span>
                                            <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                                                <i className="las la-user" style={{ fontSize: "8px" }} />
                                                {attachment.uploadedByName || user?.name || "Unknown User"}
                                            </span>
                                        </div>
                                    </div>

                                    {/* File Size - Dedicated Column */}
                                    <div style={{ 
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "500",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px"
                                    }}>
                                        <i className="las la-weight" style={{ fontSize: "11px", color: "#9ca3af" }} />
                                        <span>{formatFileSize(attachment.fileSize)}</span>
                                    </div>

                                    {/* Uploaded By - Dedicated Column */}
                                    <div style={{ 
                                        fontSize: "10px",
                                        color: "#6b7280",
                                            fontWeight: "500",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px"
                                    }}>
                                        <div style={{
                                            width: "22px",
                                            height: "22px",
                                            borderRadius: "50%",
                                            background: "linear-gradient(135deg, #3bac75 0%, #2d8f5f 100%)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: "white",
                                            fontWeight: "600",
                                            fontSize: "8px",
                                            flexShrink: 0
                                        }}>
                                            {(attachment.uploadedByName || user?.name || "U").charAt(0).toUpperCase()}
                                        </div>
                                        <span style={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap"
                                        }}>
                                            {attachment.uploadedByName || user?.name || "Unknown User"}
                                        </span>
                                    </div>

                                    {/* Download Action */}
                                    <div style={{ 
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center"
                                    }}>
                                        <button
                                            onClick={() => handleDownload(attachment)}
                                            style={{
                                                background: "#ffffff",
                                                color: "#3bac75",
                                                border: "1px solid #3bac75",
                                                padding: "7px 13px",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                fontSize: "9px",
                                                fontWeight: "600",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "6px",
                                                transition: "all 0.2s",
                                                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                                                whiteSpace: "nowrap"
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = "#3bac75";
                                            e.currentTarget.style.color = "white";
                                                e.currentTarget.style.transform = "translateY(-1px)";
                                                e.currentTarget.style.boxShadow = "0 3px 4px rgba(59, 172, 117, 0.3)";
                                        }}
                                        onMouseLeave={(e) => {
                                                e.currentTarget.style.background = "#ffffff";
                                            e.currentTarget.style.color = "#3bac75";
                                                e.currentTarget.style.transform = "translateY(0)";
                                                e.currentTarget.style.boxShadow = "0 1px 2px rgba(0,0,0,0.05)";
                                        }}
                                    >
                                            <i className="las la-download" style={{ fontSize: "10px" }} /> Download
                                    </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Details;
